import { isMobile } from 'react-device-detect';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { Inject, Service } from 'typedi';
import { 
  AuthService, DATE_FORMAT, FilterService, IScheduleCalendarDayResponse,
  IScheduleCalendarMeeting,IScheduleCalendarMeetingResponse, IScheduleMonth, 
  ScheduleCalendarRequest, getMinifiedMeetingFromDto, getStartDayTime, meetings, 
} from '../../../utils';
import { ProfileViewModel } from '../../profilePage';
import { 
  addDays, addMinutes, addMonths, format, startOfDay, endOfMonth,
  subMinutes
} from 'date-fns';
import { CalendarViewModel } from '../CalendarViewModel';
import { RoomsStore } from '../../../store';
import { MiniCalendarService } from '../../../utils/services/MiniCalendarService';

const countOfLoadableMonth = 6;

@Service()
export class ScheduleViewModel {
  @Inject(() => ScheduleCalendarRequest)
  private scheduleRequest: ScheduleCalendarRequest;

  //TODO: Отсюда импортируются часовые пояса, надо бы вынести в будущем работу с ними в отдельный сервис
  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  //TODO: Отсюда импортируется только текущий день, желательно вынести это в отдельный сервис в бдущем
  @Inject (() => CalendarViewModel)
  private calendarViewModel: CalendarViewModel; 

  @Inject (() => RoomsStore)
  private roomStore: RoomsStore;

  @Inject(() => FilterService)
  private filterService: FilterService;

  @Inject(() => MiniCalendarService)
  private miniCalendarService: MiniCalendarService;

  constructor() {
    makeObservable(this);
    
    reaction (
      () => this.filterService.selectedItem,
      () => isMobile && this.getSelectedUserOrRoom(),
    );

    reaction (
      () => this.miniCalendarService.selectedDate,
      () => this.getSelectedUserOrRoom(),
    );

    reaction (
      () => this.filterService.notCheckedCalendarsIds,
      () => isMobile && this.filterSchedule()
    );
  }

  /**
   * Объект расписания, в нем хранится информация по месяцам и датам
   */
  @observable.shallow schedule: IScheduleMonth[] = [];

  @observable.shallow filteredSchedule: IScheduleMonth[] = [];

  // Последняя загруженная дата
  @observable lastLoadedDate: Date = null;

  /**
   * Идет ли загрузка митингов?
   */
  @observable isLoading = false;

  /**
   * Текущий день.
   */
  @computed get today() {
    return this.calendarViewModel.today;
  }

  /**
   * Функция получения расписания выбранного пользователя или КФЗ.
   */
  @action getSelectedUserOrRoom = () => {
    this.clearSchedule();
    this.getSchedule();
  };

  /** Очистка расписания. */
  @action clearSchedule = () => {
    this.schedule = [];
    this.filteredSchedule = [];
    this.lastLoadedDate = null;
  }

  @action getMeetingsFromStartDay = () => {
    this.clearSchedule();
    this.getSchedule();
  }

  /**
   * Функция отправки запроса для получения расписания КФЗ.
   * @param start С какого числа загрузить.
   * @param end По какое число загрузить.
   * @returns Промис загрузки митингов для расписания.
   */
  getRoomMeetings = (start: string, end: string) => {
    const room = this.roomStore.getRoomByCabinetNumber(this.filterService.selectRoomCabinetNumber).conferenceType;

    return this.scheduleRequest.getScheduleMeetings({
      startIntervalDate: start,
      endIntervalDate: end,
      conferenceRoom: room,
    });
  }

  /**
   * Функция отправки запроса для получения расписания пользователя.
   * @param start С какого числа загрузить.
   * @param end По какое число загрузить.
   * @returns Промис загрузки митингов для расписания.
   */
  getPeopleMeetings = (start: string, end: string) => this.scheduleRequest.getScheduleMeetings({
    userIds: [this.filterService.selectEmployeeId ?? AuthService.getUserId()],
    startIntervalDate: start,
    endIntervalDate: end,
  });

  /** 
   * Функция получения митингов для расписания.
   */
  @action getSchedule = () => {
    if(!this.isLoading) {
      this.isLoading = true;

      //запускаем инициализацию профиля, если еще не началась
      this.profileViewModel.initPromise === null && this.profileViewModel.onInit();
      this.profileViewModel.initPromise.then(() => runInAction(() => {
        const {startIntervalDate, endIntervalDate, endDate} = this.getIntervalsSettings();
        
        const allMeetings = this.filterService.selectRoomCabinetNumber
          ? this.getRoomMeetings(startIntervalDate, endIntervalDate)
          : this.getPeopleMeetings(startIntervalDate, endIntervalDate);

        allMeetings.then((res) => runInAction(() => {
          const meetingsFromResponse = res.data as IScheduleCalendarMeetingResponse[];
          const lastLoadedIndex = this.schedule.length - 1;
          const startMonth = this.lastLoadedDate ? addDays(this.lastLoadedDate, 1) : this.miniCalendarService.selectedDate ?? this.today;
          this.insertEmptyMonth(
            this.lastLoadedDate 
              ? countOfLoadableMonth 
              : countOfLoadableMonth + 1,
            this.getMonths(startMonth)
          );
          this.insertSchedule(meetingsFromResponse, lastLoadedIndex);
          this.lastLoadedDate = endDate;
          this.isLoading = false;            
        }));
      }))
    }
  }

  /**
   * Получение настроек времени для запроса расписания
   * @returns startIntervalDate - С какого числа надо загрузить, 
   * endIntervalDate – По какое число надо загрузить,
   * endDate - Конечная дата.
   */
  getIntervalsSettings = () => {
    const startDate = !this.lastLoadedDate
      ? getStartDayTime(this.miniCalendarService.selectedDate ?? this.today, this.profileViewModel.minutesOffset)
      : getStartDayTime(addDays(this.lastLoadedDate, 1), this.profileViewModel.minutesOffset);

    //конечная дата это спустя три месяца, вычитаем секунду, чтобы не было дублирования
    const endDate = subMinutes(
      endOfMonth(addMonths(startDate, countOfLoadableMonth)),
      this.profileViewModel.minutesOffset
    );

    //добавляем системное время, для правильного перевода в строку (при переводе оно вычитается)
    const systemOffset = -startDate.getTimezoneOffset();
    const startIntervalDate = addMinutes(startDate, systemOffset).toISOString();
    const endIntervalDate = addMinutes(endDate, systemOffset).toISOString();

    return {
      startIntervalDate, 
      endIntervalDate,
      endDate
    }
  }

  /**
   * Вспомогательная функция вставки митингов, в которой получаются данные 
   * и распределяются в объект {date, meetings}.
   * @param start С какого числа загрузить.
   * @param end По какое число загрузить.
   */
  @action insertSchedule = (meetingsFromResponse:IScheduleCalendarMeetingResponse[], lastIndex: number) => {
    if (lastIndex === -1) {
      lastIndex++;
    }

    meetingsFromResponse.forEach((item) => {
      //получаем митинг
      const meeting = getMinifiedMeetingFromDto(item, this.profileViewModel.minutesOffset);
      let lastInsertedData = this.schedule[lastIndex];
      const meetingMonth = this.getMonths(meeting.startTime);
      const isNextMonth = lastInsertedData.months !== meetingMonth;

      if (isNextMonth === false) {
        this.setMonthMeeting(lastInsertedData.monthMeetings, meeting);
      } else {
        lastIndex++;
        this.setMonthMeeting(this.schedule[lastIndex].monthMeetings, meeting);
      }
    });

    this.filterAddedScheduleMonth();
  }
  
  /**
   * Вспомогательная функция установки митингов, на месяц.
   * Распределяются в объект {date, meetings}
   * @param monthMeetings Ссылка на митинги месяца.
   * @param meeting митинг, который нужно вставить.
   */
  @action setMonthMeeting = (monthMeetings: IScheduleCalendarDayResponse[] , meeting: IScheduleCalendarMeeting) => {
    //этот митинг на следующем дне, или все еще относится к предыдущему
    const isCurrentDay = monthMeetings.length > 0 && (
      format(monthMeetings[monthMeetings.length - 1]?.date, DATE_FORMAT) === format(meeting.startTime, DATE_FORMAT)
    );

    //если на следующем дне, то вставляем как новую дату
    //либо пихаем в предыдущий день
    if (isCurrentDay === false) {
      monthMeetings.push({date: startOfDay(meeting.startTime), meetings: [meeting]})
    } else {
      monthMeetings[monthMeetings.length - 1].meetings.push(meeting);
    }
  }

  /**
   * Инициализация загружаемых месяцев
   * @param count количество месяцев, которые нужно вставить
   * @param startMonth стартовый месяц от которого пойдет отсчет номера
   */
  @action insertEmptyMonth = (count: number, startMonth: number) => {
    for (let i = 0; i < count; i++) {
      this.schedule.push({ months: startMonth + i, monthMeetings: [] })
    }
  }

  /**
   * Функция высчитывания номера месяца
   * @param date Дата, месяц которой надо посчитать
   * @returns Номер месяца, который можно декодировать
   */
  getMonths = (date: Date) => date.getMonth() + date.getFullYear() * 12;

  @action filterSchedule = () => {
    if (this.filterService.notCheckedCalendarsIds.length !== 0) {
      this.filteredSchedule = this.schedule.map((scheduleMonth) => 
        this.getFilteredMeetingsInMonth(scheduleMonth)
      );
    } else {
      this.filteredSchedule = this.schedule;
    }
  }

  getFilteredMeetingsInDay = (day: IScheduleCalendarDayResponse) => {
    const filteredMeetings = day.meetings.filter(meeting => (
      !this.filterService.notCheckedCalendarsIds.includes(meeting.calendarId))
    );
    if (filteredMeetings.length !== 0) 
      return { date: day.date, meetings: filteredMeetings }
  }

  getFilteredMeetingsInMonth = (scheduleMonth: IScheduleMonth):IScheduleMonth => { //1 месяц
    const monthMeetings = scheduleMonth.monthMeetings
      .map(day => this.getFilteredMeetingsInDay(day))
      .filter(day => day !== undefined);
    return { months: scheduleMonth.months, monthMeetings }
  }

  @action filterAddedScheduleMonth = () => {
    const addedScheduleMonths = this.schedule.slice(this.filteredSchedule.length);
    const filtered = addedScheduleMonths.map<IScheduleMonth>(scheduleMonth => (
      this.getFilteredMeetingsInMonth(scheduleMonth)
    ));
    this.filteredSchedule.push(...filtered);
  }
}